import { useQuery } from '@tanstack/react-query'
import { endOfDay, format, parseISO, startOfDay } from 'date-fns'
import { supabase } from '@/integrations/supabase/client'
import { useCompany } from '@/lib/CompanyProvider'
import type { Order } from './KanbanCard'

export function ArchivedOrdersTable({ dateFilter }: { dateFilter: string }) {
  const { activeCompanyId } = useCompany()

  const {
    data: orders = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['archived_orders', activeCompanyId, dateFilter],
    queryFn: async () => {
      if (!activeCompanyId) return []

      let query = supabase
        .from('orders')
        .select('*')
        .eq('company_id', activeCompanyId)
        .neq('status', 'ORCAMENTO')
        .order('created_at', { ascending: false })

      if (dateFilter === 'today') {
        const now = new Date()
        const startIso = startOfDay(now).toISOString()
        const endIso = endOfDay(now).toISOString()
        query = query.or(
          `and(created_at.gte.${startIso},created_at.lte.${endIso}),and(paid_at.gte.${startIso},paid_at.lte.${endIso})`
        )
      } else if (dateFilter === 'yesterday') {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        const startIso = startOfDay(yesterday).toISOString()
        const endIso = endOfDay(yesterday).toISOString()
        query = query.or(
          `and(created_at.gte.${startIso},created_at.lte.${endIso}),and(paid_at.gte.${startIso},paid_at.lte.${endIso})`
        )
      } else if (dateFilter === 'last3days') {
        const threeDaysAgo = new Date()
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 2)
        const startIso = startOfDay(threeDaysAgo).toISOString()
        const endIso = endOfDay(new Date()).toISOString()
        query = query.or(
          `and(created_at.gte.${startIso},created_at.lte.${endIso}),and(paid_at.gte.${startIso},paid_at.lte.${endIso})`
        )
      } else if (dateFilter !== 'active' && dateFilter) {
        const [y, m, d] = dateFilter.split('-')
        const localDate = new Date(Number(y), Number(m) - 1, Number(d))
        const startIso = startOfDay(localDate).toISOString()
        const endIso = endOfDay(localDate).toISOString()
        query = query.or(
          `and(created_at.gte.${startIso},created_at.lte.${endIso}),and(paid_at.gte.${startIso},paid_at.lte.${endIso})`
        )
      }

      const { data, error } = await query
      if (error) throw error
      return data as Order[]
    },
    enabled: !!activeCompanyId && dateFilter !== 'active',
  })

  const totalRevenue = orders.reduce((acc, o) => acc + (o.total || 0), 0)

  if (isLoading) return <div className="p-4 text-gray-400">Carregando...</div>

  if (queryError) {
    return (
      <div className="p-12 text-red-400">
        <p>Erro ao buscar: {(queryError as Error).message}</p>
        <p className="text-xs mt-2">Filtro: {dateFilter}</p>
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-gray-400">
        <p>Nenhuma Ordem de Serviço encontrada para esta data.</p>
        <p className="text-xs text-gray-600 mt-2">
          Debug Date: {dateFilter} | ID: {activeCompanyId}
        </p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="bg-slate-900/60 rounded-xl border border-slate-800 overflow-hidden">
        <table className="w-full text-left text-sm text-slate-300">
          <thead className="text-xs uppercase bg-slate-800/80 text-slate-400 border-b border-slate-700">
            <tr>
              <th className="px-4 py-3">Placa / Modelo</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Criação / Pagamento</th>
              <th className="px-4 py-3 text-right">Valor</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {orders.map((order) => (
              <tr key={order.id} className="hover:bg-slate-800/40 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-bold text-slate-200">{order.plate}</div>
                  <div className="text-xs text-slate-500">
                    {order.vehicle_model || 'Não informado'}
                  </div>
                </td>
                <td className="px-4 py-3">{order.customer_name || 'Avulso'}</td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-1 rounded-md text-xs font-semibold ${
                      order.status === 'PAGO'
                        ? 'bg-emerald-900/50 text-emerald-400'
                        : order.status === 'CANCELADO'
                          ? 'bg-red-900/50 text-red-400'
                          : 'bg-slate-800 text-slate-300'
                    }`}
                  >
                    {order.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-400">
                  C: {format(parseISO(order.created_at), 'dd/MM/yy HH:mm')}
                  {order.paid_at && <br />}
                  {order.paid_at && `P: ${format(parseISO(order.paid_at), 'dd/MM/yy HH:mm')}`}
                </td>
                <td className="px-4 py-3 font-bold text-emerald-400 text-right">
                  R$ {order.total?.toFixed(2) || '0.00'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-900 font-bold border-t border-slate-700">
            <tr>
              <td colSpan={4} className="px-4 py-3 text-right">
                Faturamento Total do Dia:
              </td>
              <td className="px-4 py-3 text-right text-emerald-400 text-lg">
                R$ {totalRevenue.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
